import { Container, Grid } from '@material-ui/core';
import { showSnackbar } from 'components/root-snackbar';
import { FC, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app';
import { Redirect } from 'react-router-dom';
import { getPreLoginToken } from '../authentication-slice';
import useStyles from './login-style';
import { Box, CircularProgress } from '@mui/material';

const Login: FC = () => {
  const cacheBuster = `cb_${Date.now()}`;
  const classes: any = useStyles();
  const dispatch = useAppDispatch();
  const IDMS_EMAIL_LOGIN_PAGE = process.env.REACT_APP_IDMS_EMAIL_LOGIN_PAGE || '';
  const IDMS_CLASSIC_LOGIN_PAGE = process.env.REACT_APP_IDMS_CLASSIC_LOGIN_PAGE || '';

  const loggedIn = useAppSelector((state) => state.user.loggedIn);
  const [loginType, setLoginType] = useState<'classic' | 'email'>('classic');
  const [preLoginTokenValue, setPreLoginToken] = useState<string | null>(null);

  // Redirect to Email Login iframe after email confirmation
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('emailConfirmed') === 'true') {
      setLoginType('email');
      dispatch(showSnackbar({ type: 'success', message: 'Email confirmed successfully' }));
      // remove the param from URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  // Get Pre Login Token On Component Mount
  useEffect(() => {
    const getPreLoginTokenOnMount = async () => {
      const result = await dispatch(getPreLoginToken()).unwrap();
      if (result?.preLoginToken) {
        setPreLoginToken(result.preLoginToken);
      } else {
        dispatch(showSnackbar({ type: 'error', message: 'Failed to get PreLoginToken' }));
      }
    };

    getPreLoginTokenOnMount();
  }, [dispatch]);

  // Parent: Listen for handshake requests
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Iframe requests the token
      if (event.data?.type === 'REQUEST_PRE_LOGIN_TOKEN' && preLoginTokenValue) {
        (event.source as WindowProxy)?.postMessage(
          { type: 'PRE_LOGIN_TOKEN', token: preLoginTokenValue },
          event.origin as string,
        );
      }

      // Iframe asks to switch login mode
      if (event.data === 'USE_EMAIL_LOGIN') {
        setLoginType('email');
      }

      if (event.data === 'USE_CLASSIC_LOGIN') {
        setLoginType('classic');
      }

      // Successful login from iframe
      if (event.data?.type === 'LOGIN_SUCCESS') {
        if (event.data?.access_token) {
          // For testing, logging the event data object
          console.log('Login successfull:', event.data);
          const origin = window.location.origin;
          const targetUrl = `${origin}/?token=${encodeURIComponent(event.data.access_token)}`;
          window.location.href = targetUrl;
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [preLoginTokenValue]);

  // Automatically reloads the login page every 55 minutes to get the fresh PreLoginToken for Login API.
  // The interval is cleared when the component unmounts to avoid memory leaks.
  useEffect(() => {
    const intervalId = setInterval(() => {
      window.location.reload();
    }, 3300000); // 55 minutes

    return () => clearInterval(intervalId);
  }, []);

  return (
    <Container component="div" className={classes.container}>
      <Grid container direction="column" justifyContent="center" alignItems="center" className={classes.grid}>
        {!preLoginTokenValue ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh" width="100%">
            <CircularProgress />
          </Box>
        ) : (
          <Grid item className={classes.gridItem}>
            {loginType === 'classic' && (
              <iframe
                src={`${IDMS_CLASSIC_LOGIN_PAGE}?hostOrigin=${encodeURIComponent(window.location.origin)}&cacheBuster=${cacheBuster}`}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
                title="Classic Login"
                onLoad={() => {
                  if (preLoginTokenValue) {
                    const iframe = document.querySelector('iframe');
                    iframe?.contentWindow?.postMessage(
                      { type: 'PRE_LOGIN_TOKEN', token: preLoginTokenValue },
                      IDMS_CLASSIC_LOGIN_PAGE,
                    );
                  }
                }}
              />
            )}

            {loginType === 'email' && (
              <iframe
                src={`${IDMS_EMAIL_LOGIN_PAGE}?hostOrigin=${encodeURIComponent(window.location.origin)}&cacheBuster=${cacheBuster}`}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
                title="Email Login"
                onLoad={() => {
                  if (preLoginTokenValue) {
                    const iframe = document.querySelector('iframe');
                    iframe?.contentWindow?.postMessage(
                      { type: 'PRE_LOGIN_TOKEN', token: preLoginTokenValue },
                      IDMS_EMAIL_LOGIN_PAGE,
                    );
                  }
                }}
              />
            )}
          </Grid>
        )}
      </Grid>
      {loggedIn && <Redirect to={'/'} />}
    </Container>
  );
};

export default Login;
